import React, { createContext, useContext, useState, useCallback } from 'react';

const SellerContext = createContext();

export const SellerProvider = ({ children }) => {
  // Registration form data
  const [formData, setFormData] = useState({
    // Mobile verification
    mobileNumber: '',
    otp: '',
    
    // Business details
    hasGST: null,
    gstNumber: '',
    panNumber: '',
    fullName: '',
    email: '',
    address: '',
    state: '',
    pincode: '',
    district: '',
    city: '',
    buildingNumber: '',
    street: '',
    landmark: '',
    
    // Pickup address
    pickupAddress: {
      buildingNumber: '',
      street: '',
      landmark: '',
      pincode: '',
      city: '',
      state: '',
      useEnrolmentAddress: false,
    },
    
    // Bank details
    accountNumber: '',
    confirmAccountNumber: '',
    ifscCode: '',
    bankDetails: {
      beneficiaryName: '',
      accountNumber: '',
      bankName: '',
      ifscCode: '',
    },
    
    // Supplier details
    storeName: '',
    supplierFullName: '',
    supplierEmail: '',
    agreeToTerms: false,
    
    // Enrolment ID
    enrolmentId: '',
    enrolmentDetails: null,
  });

  const [currentStep, setCurrentStep] = useState(1);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [verificationStep, setVerificationStep] = useState('mobile'); // mobile, otp, business
  const [authToken, setAuthToken] = useState(localStorage.getItem('sellerToken') || '');
  const [seller, setSeller] = useState(null);

  const updateFormData = useCallback((field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  }, []);

  const updateNestedFormData = useCallback((parentField, field, value) => {
    setFormData((prev) => ({
      ...prev,
      [parentField]: {
        ...prev[parentField],
        [field]: value,
      },
    }));
  }, []);

  const setError = useCallback((field, message) => {
    setErrors((prev) => ({
      ...prev,
      [field]: message,
    }));
  }, []);

  const clearErrors = useCallback(() => {
    setErrors({});
  }, []);

  const clearAllErrors = useCallback((field) => {
    if (field) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    } else {
      setErrors({});
    }
  }, []);

  const nextStep = useCallback(() => {
    setCurrentStep((prev) => prev + 1);
  }, []);

  const prevStep = useCallback(() => {
    setCurrentStep((prev) => (prev > 1 ? prev - 1 : 1));
  }, []);

  const goToStep = useCallback((step) => {
    setCurrentStep(step);
  }, []);

  const resetForm = useCallback(() => {
    setFormData({
      mobileNumber: '',
      otp: '',
      hasGST: null,
      gstNumber: '',
      panNumber: '',
      fullName: '',
      email: '',
      address: '',
      state: '',
      pincode: '',
      district: '',
      city: '',
      buildingNumber: '',
      street: '',
      landmark: '',
      pickupAddress: {
        buildingNumber: '',
        street: '',
        landmark: '',
        pincode: '',
        city: '',
        state: '',
        useEnrolmentAddress: false,
      },
      accountNumber: '',
      confirmAccountNumber: '',
      ifscCode: '',
      bankDetails: {
        beneficiaryName: '',
        accountNumber: '',
        bankName: '',
        ifscCode: '',
      },
      storeName: '',
      supplierFullName: '',
      supplierEmail: '',
      agreeToTerms: false,
      enrolmentId: '',
      enrolmentDetails: null,
    });
    setCurrentStep(1);
    setErrors({});
    setOtpSent(false);
    setVerificationStep('mobile');
  }, []);

  const setAuthData = useCallback((token, sellerData) => {
    setAuthToken(token);
    setSeller(sellerData);
    localStorage.setItem('sellerToken', token);
    localStorage.setItem('sellerData', JSON.stringify(sellerData));
  }, []);

  const logout = useCallback(() => {
    setAuthToken('');
    setSeller(null);
    localStorage.removeItem('sellerToken');
    localStorage.removeItem('sellerData');
    resetForm();
  }, [resetForm]);

  const value = {
    formData,
    updateFormData,
    updateNestedFormData,
    currentStep,
    setCurrentStep,
    nextStep,
    prevStep,
    goToStep,
    errors,
    setError,
    clearErrors,
    clearAllErrors,
    loading,
    setLoading,
    successMessage,
    setSuccessMessage,
    otpSent,
    setOtpSent,
    verificationStep,
    setVerificationStep,
    resetForm,
    authToken,
    seller,
    setAuthData,
    logout,
  };

  return (
    <SellerContext.Provider value={value}>
      {children}
    </SellerContext.Provider>
  );
};

export const useSeller = () => {
  const context = useContext(SellerContext);
  if (!context) {
    throw new Error('useSeller must be used within SellerProvider');
  }
  return context;
};
